import {
	Button,
	DialogContainer,
	Modal,
	ModalFooter,
	ModalHeading,
	ModalInputBox,
	ModalTitle,
	ModalTitleContainer,
	ModelSubHeading,
	TextFieldWrapper,
} from "@factorearth/component-library";
import { ErrorMessage, Form, Formik } from "formik";
import React, { useState } from "react";
import { FiChevronRight, FiX } from "react-icons/fi";
import { useTheme } from "../../hooks/useSingleton";
import styles from "./CreateNewProject.module.scss";
import { FormTemplateForResolver, Project, ProjectForResolver } from "db/definitions";
import { useNavigate } from "react-router";
import { SelectAutoSyncTemplates } from "./SelectAutoSyncTemplates";
import handleSetOrRemoveAutoSyncFormTemplates from "utils/handleSetAutoSyncFormTemplates";

interface CreateNewProjectProps {
	formTemplates: FormTemplateForResolver[];
	group: string;
	handleCreateProject: (proj: Pick<ProjectForResolver, "name" | "description">) => Promise<Project | undefined>;
	online: boolean;
	open: boolean;
	projects: ProjectForResolver[];
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function CreateNewProject(props: CreateNewProjectProps) {
	const { group, handleCreateProject, projects, formTemplates, online, open, setOpen } = props;

	const [openSelectFormTemplates, setOpenSelectFormTemplates] = useState(false);
	const [selectedFormTemplateIds, setSelectedFormTemplateIds] = useState<string[]>([]);

	const colorPalette = useTheme();
	const navigate = useNavigate();

	const initial: Pick<ProjectForResolver, "name" | "description"> = {
		name: "",
		description: "",
	};

	const isDupProjectNameInGroup = (value: string) => {
		for (const project of projects) {
			if (project.name === value && group === project.group) {
				return true;
			}
		}
		handleSelectFormTemplates();
		return false;
	};

	const handleSelectFormTemplates = () => {
		setOpenSelectFormTemplates(!openSelectFormTemplates);
	};

	const handleClose = () => {
		setOpenSelectFormTemplates(false);
		setOpen(false);
		setSelectedFormTemplateIds([]);
	};

	return (
		<Formik
			validateOnBlur={false}
			validateOnChange={false}
			initialValues={initial}
			validate={(values) => {
				values.name = values.name.trim();
				const errors: Record<string, string> = {};
				if (!values.name) {
					errors.name = "Required";
					handleSelectFormTemplates();
				}
				const isDuplicate = isDupProjectNameInGroup(values.name);
				if (isDuplicate) errors.name = "This project name already exists";
				return errors;
			}}
			onSubmit={async (values, { setSubmitting }) => {
				setSubmitting(true);
				const created = await handleCreateProject(values);
				if (created) {
					await handleSetOrRemoveAutoSyncFormTemplates(selectedFormTemplateIds, created, "set");
				}
				handleClose();
				setSubmitting(false);
				if (created) navigate(`/project/${created.id}`);
			}}>
			{({ isSubmitting, errors, touched, handleChange }) =>
				openSelectFormTemplates ? (
					<SelectAutoSyncTemplates
						formTemplates={formTemplates}
						isSubmitting={isSubmitting}
						setOpenSelectFormTemplates={setOpenSelectFormTemplates}
						openSelectFormTemplates={openSelectFormTemplates}
						handleClose={handleClose}
						setSelectedFormTemplateIds={setSelectedFormTemplateIds}
						selectedFormTemplateIds={selectedFormTemplateIds}
					/>
				) : (
					<Modal colorPalette={colorPalette} open={open} setOpen={setOpen}>
						<Form>
							<DialogContainer>
								<ModalTitleContainer>
									<ModalTitle>Create a New Project</ModalTitle>
									<FiX onClick={handleClose} size="24" />
								</ModalTitleContainer>
								<ModelSubHeading>Name your project. Be specific Use year, month, data, and unique identifiers like location.</ModelSubHeading>
								<ModalInputBox>
									<ModalHeading>Project Name</ModalHeading>
									<TextFieldWrapper
										type="text"
										name="name"
										id="project-name"
										data-test="project-name"
										colorPalette={colorPalette}
										onChange={handleChange}
										tabIndex={-1}
									/>
									<ErrorMessage
										name="name"
										component="div"
										// @ts-ignore
										style={{
											color: colorPalette.utils.destructive,
										}}
									/>
								</ModalInputBox>
								<ModalInputBox>
									<ModalHeading>Project Description (optional)</ModalHeading>
									<TextFieldWrapper
										type="text"
										name="description"
										id="project-description"
										data-test="project-description"
										colorPalette={colorPalette}
										onChange={handleChange}
										tabIndex={-1}
									/>
									<ErrorMessage
										name="description"
										component="div"
										// @ts-ignore
										style={{
											color: colorPalette.utils.destructive,
										}}
									/>
								</ModalInputBox>
								<ModalFooter>
									{online ? (
										<Button
											type="button"
											data-test={"select-templates-for-auto-sync"}
											colorPalette={colorPalette}
											onClick={handleSelectFormTemplates}
											variant="filled"
											label="Select Form Templates"
											icon={<FiChevronRight />}
										/>
									) : (
										<Button
											type="submit"
											disabled={isSubmitting}
											data-test={"create-project-submit"}
											colorPalette={colorPalette}
											className={styles.submit_btn}
											variant="outlined"
											label="Submit"
										/>
									)}
								</ModalFooter>
							</DialogContainer>
						</Form>
					</Modal>
				)
			}
		</Formik>
	);
}

export default CreateNewProject;
